import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive, ArchiveRestore, ChevronLeft, FolderX, MessageCircle, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Composer } from "@/components/chat/Composer";
import { DeleteProjectDialog } from "@/components/projects/DeleteProjectDialog";
import { ProjectFiles } from "@/components/projects/ProjectFiles";
import { ProjectFormDialog } from "@/components/projects/ProjectFormDialog";
import { useMe, useProject, useUpdateProject } from "@/hooks/queries";
import { useUploads } from "@/hooks/useUploads";
import { useResearchMode } from "@/hooks/useResearchMode";
import { startStream } from "@/lib/chatStore";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "chats", label: "Chats" },
  { key: "files", label: "Files" },
  { key: "instructions", label: "Instructions" },
];

function ProjectMenu({ project, onEdit, onDelete }) {
  const update = useUpdateProject();
  const archived = project.status === "archived";
  const toggle = () =>
    update.mutateAsync({ id: project.id, status: archived ? "active" : "archived" }).then(() => toast.success(archived ? "Project restored" : "Project archived")).catch((e) => toast.error(e.message));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Project options" className="rounded-md p-1.5 text-zinc-500 hover:bg-white/[0.06] hover:text-white" data-testid="project-options-button">
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-white/10 bg-ink-850">
        <DropdownMenuItem onSelect={onEdit} className="gap-2 focus:bg-white/[0.06]" data-testid="project-edit-option"><Pencil className="h-4 w-4" /> Edit details</DropdownMenuItem>
        <DropdownMenuItem onSelect={toggle} className="gap-2 focus:bg-white/[0.06]" data-testid="project-archive-option">
          {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />} {archived ? "Restore" : "Archive"}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onDelete} className="gap-2 text-rose-300 focus:bg-rose-500/10 focus:text-rose-200" data-testid="project-delete-option"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProjectChats({ chats }) {
  if (!chats.length) return <p className="py-8 text-center text-sm text-zinc-500" data-testid="project-chats-empty">No chats yet. Ask something above to start the first one.</p>;
  return (
    <div className="space-y-0.5" data-testid="project-chats">
      {chats.map((c) => (
        <Link key={c.id} to={`/chat/${c.id}`} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-white/[0.035]" data-testid={`project-chat-${c.id}`}>
          <MessageCircle className="h-4 w-4 shrink-0 text-zinc-600" strokeWidth={1.6} />
          <span className="min-w-0 flex-1 truncate text-[14.5px] text-zinc-100">{c.title}</span>
          <span className="shrink-0 text-[12px] text-zinc-600">{formatRelative(c.last_message_at)}</span>
        </Link>
      ))}
    </div>
  );
}

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useProject(projectId);
  const { data: me } = useMe();
  const [tab, setTab] = useState("chats");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const uploads = useUploads();
  const research = useResearchMode();
  const project = data?.project;

  if (error?.status === 404)
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center" data-testid="project-not-found">
        <FolderX className="h-8 w-8 text-zinc-600" strokeWidth={1.5} />
        <h2 className="mt-4 text-base font-medium text-white md:text-lg">Project not found</h2>
        <p className="mt-1.5 text-sm text-zinc-500">It may have been deleted, or it belongs to another account.</p>
        <Link to="/projects" className="mt-6 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950">All projects</Link>
      </div>
    );

  const send = () => {
    if (research.on) {
      const question = draft;
      setDraft("");
      research.start({ question, projectId }).then((res) => !res && setDraft(question));
      return;
    }
    const key = startStream({ content: draft, kind: "message", attachments: uploads.attachable, projectId, mode: research.chatMode });
    setDraft("");
    uploads.clear();
    navigate(`/chat?pending=${key}`);
  };
  const archived = project?.status === "archived";

  return (
    <div className="h-full overflow-y-auto" data-testid="project-page">
      <div className="mx-auto w-full max-w-[860px] px-4 pb-16 pt-6 sm:px-6 sm:pt-10">
        <Link to="/projects" className="inline-flex items-center gap-1 text-[13px] text-zinc-500 hover:text-zinc-200" data-testid="project-back-link"><ChevronLeft className="h-4 w-4" /> Projects</Link>
        {isLoading && <div className="mt-6 h-8 w-1/2 animate-pulse rounded bg-white/[0.05]" />}
        {project && (
          <>
            <div className="mt-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="break-words text-[1.7rem] font-semibold tracking-tight text-white sm:text-3xl" data-testid="project-name">{project.name}</h1>
                {project.description && <p className="mt-1.5 text-sm text-zinc-400" data-testid="project-description">{project.description}</p>}
                {archived && <p className="mt-2 text-xs text-amber-200/80" data-testid="project-archived-note">Archived — restore it to start new chats.</p>}
              </div>
              <ProjectMenu project={project} onEdit={() => setEditing(true)} onDelete={() => setDeleting(true)} />
            </div>
            {!archived && (
              <div className="mt-7">
                <Composer value={draft} onChange={setDraft} onSubmit={send} variant="hero" placeholder={`Ask anything in ${project.name}…`} sendKey={me?.settings?.send_key} uploads={uploads} research={research} testIdPrefix="project-composer" disabled={me?.credits?.balance < 1 || research.starting} />
              </div>
            )}
            <div className="mt-9 flex gap-5 border-b border-white/[0.07]" data-testid="project-tabs">
              {TABS.map((t) => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`project-tab-${t.key}`} className={cn("-mb-px border-b-2 pb-2.5 text-sm transition-colors", tab === t.key ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-200")}>
                  {t.label}
                  {t.key === "chats" && <span className="ml-1.5 text-zinc-600">{project.chat_count}</span>}
                  {t.key === "files" && <span className="ml-1.5 text-zinc-600">{project.file_count}</span>}
                </button>
              ))}
            </div>
            <div className="mt-4">
              {tab === "chats" && <ProjectChats chats={data.chats} />}
              {tab === "files" && <ProjectFiles projectId={projectId} files={data.files} />}
              {tab === "instructions" && (
                <div className="rounded-2xl border border-white/[0.08] bg-ink-900 p-5" data-testid="project-instructions">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{project.instructions || "No instructions yet. Tell ChatSleek how to work in this project — tone, audience, goals or constraints."}</p>
                  <button type="button" onClick={() => setEditing(true)} className="mt-4 rounded-full border border-white/12 px-3.5 py-1.5 text-[13px] text-zinc-200 hover:bg-white/[0.05]" data-testid="project-instructions-edit">Edit instructions</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {editing && project && <ProjectFormDialog open onOpenChange={setEditing} project={project} />}
      {project && <DeleteProjectDialog project={project} open={deleting} onOpenChange={setDeleting} />}
    </div>
  );
}
